import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Loader2, Layers, Save, Plus, GripVertical, ImageIcon, Trash2, BookOpen } from "lucide-react";

function reorder(list, from, to) {
  const next = [...list];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next;
}

function IssueCard({ journalSlug, issue, reload }) {
  const [meta, setMeta] = useState({ title: issue.title || "", cover_image: issue.cover_image || "", editorial_note: issue.editorial_note || "" });
  const [papers, setPapers] = useState(issue.papers);
  const [savingMeta, setSavingMeta] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);

  useEffect(() => { setPapers(issue.papers); }, [issue.papers]);

  const saveMeta = async () => {
    setSavingMeta(true);
    try {
      await api.put("/issues/meta", { journal_slug: journalSlug, volume: issue.volume, issue: issue.issue, ...meta });
      toast.success("Issue details saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSavingMeta(false); }
  };

  const persistOrder = async (list) => {
    try {
      await api.put("/issues/order", { journal_slug: journalSlug, volume: issue.volume, issue: issue.issue, paper_ids: list.map((p) => p.id) });
      toast.success("Article order updated");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const onDrop = (to) => {
    if (dragIdx === null || dragIdx === to) { setDragIdx(null); return; }
    const next = reorder(papers, dragIdx, to);
    setPapers(next);
    setDragIdx(null);
    persistOrder(next);
  };

  const move = (i, dir) => {
    const to = i + dir;
    if (to < 0 || to >= papers.length) return;
    const next = reorder(papers, i, to);
    setPapers(next);
    persistOrder(next);
  };

  const removeIssue = async () => {
    if (!window.confirm("Delete this empty issue?")) return;
    try {
      await api.delete(`/issues?journal=${journalSlug}&volume=${issue.volume}&issue=${issue.issue}`);
      toast.success("Issue deleted");
      reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="bg-card rounded-xl border border-slate-200 dark:border-slate-800 overflow-hidden" data-testid={`issue-card-${issue.volume}-${issue.issue}`}>
      <div className="flex items-center justify-between gap-3 px-5 py-3 border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/50">
        <h3 className="font-serif font-bold text-lg text-slate-900 dark:text-slate-100">
          Volume {issue.volume} · Issue {issue.issue}
          <span className="text-sm font-sans font-normal text-slate-400 ml-2">{papers.length} article{papers.length !== 1 ? "s" : ""}</span>
        </h3>
        {papers.length === 0 && (
          <Button variant="ghost" size="icon" onClick={removeIssue} data-testid={`delete-issue-${issue.volume}-${issue.issue}`}><Trash2 className="w-4 h-4 text-destructive" /></Button>
        )}
      </div>

      <div className="p-5 grid md:grid-cols-[150px_1fr] gap-5">
        <div>
          <div className="w-full aspect-[3/4] rounded-lg border border-slate-200 dark:border-slate-800 bg-slate-100 dark:bg-slate-800 overflow-hidden grid place-items-center"
            style={meta.cover_image ? { backgroundImage: `url(${meta.cover_image})`, backgroundSize: "cover", backgroundPosition: "center" } : {}}>
            {!meta.cover_image && <ImageIcon className="w-7 h-7 text-slate-300" />}
          </div>
        </div>
        <div className="space-y-3">
          <div><Label className="text-xs">Issue title (optional)</Label><Input value={meta.title} onChange={(e) => setMeta({ ...meta, title: e.target.value })} className="mt-1 h-9" placeholder="e.g. Special Collection on AI" data-testid={`issue-title-${issue.volume}-${issue.issue}`} /></div>
          <div><Label className="text-xs">Cover image URL</Label><Input value={meta.cover_image} onChange={(e) => setMeta({ ...meta, cover_image: e.target.value })} className="mt-1 h-9" placeholder="https://…/cover.jpg" data-testid={`issue-cover-${issue.volume}-${issue.issue}`} /></div>
          <div><Label className="text-xs">Editorial note</Label><Textarea value={meta.editorial_note} onChange={(e) => setMeta({ ...meta, editorial_note: e.target.value })} rows={2} className="mt-1" placeholder="A short note introducing this issue…" data-testid={`issue-note-${issue.volume}-${issue.issue}`} /></div>
          <Button onClick={saveMeta} disabled={savingMeta} className="rounded-full gap-1.5 h-9" data-testid={`issue-savemeta-${issue.volume}-${issue.issue}`}>
            {savingMeta ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4" /> Save issue details</>}
          </Button>
        </div>
      </div>

      {papers.length > 0 && (
        <div className="px-5 pb-5">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Table of contents — drag to reorder</p>
          <ul className="space-y-2">
            {papers.map((p, i) => (
              <li
                key={p.id}
                draggable
                onDragStart={() => setDragIdx(i)}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => onDrop(i)}
                data-testid={`toc-item-${p.id}`}
                className={`flex items-center gap-3 rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-3 cursor-grab active:cursor-grabbing ${dragIdx === i ? "opacity-50" : ""}`}
              >
                <GripVertical className="w-4 h-4 text-slate-400 shrink-0" />
                <span className="text-sm font-medium text-slate-700 dark:text-slate-300 truncate flex-1">{i + 1}. {p.title}</span>
                <div className="flex gap-1 shrink-0">
                  <button onClick={() => move(i, -1)} disabled={i === 0} data-testid={`toc-up-${p.id}`} className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-30">↑</button>
                  <button onClick={() => move(i, 1)} disabled={i === papers.length - 1} data-testid={`toc-down-${p.id}`} className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-30">↓</button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function AddIssueDialog({ journalSlug, reload }) {
  const [open, setOpen] = useState(false);
  const [vol, setVol] = useState(1);
  const [iss, setIss] = useState(1);
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);

  const create = async () => {
    setBusy(true);
    try {
      await api.post("/issues", { journal_slug: journalSlug, volume: Number(vol), issue: Number(iss), title });
      toast.success("Issue created");
      setOpen(false); setTitle(""); reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button className="rounded-full gap-1.5" data-testid="add-issue-btn"><Plus className="w-4 h-4" /> Add Volume / Issue</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle className="font-serif">New Volume / Issue</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Volume</Label><Input type="number" min="1" value={vol} onChange={(e) => setVol(e.target.value)} className="mt-1" data-testid="new-issue-vol" /></div>
            <div><Label>Issue</Label><Input type="number" min="1" value={iss} onChange={(e) => setIss(e.target.value)} className="mt-1" data-testid="new-issue-iss" /></div>
          </div>
          <div><Label>Title (optional)</Label><Input value={title} onChange={(e) => setTitle(e.target.value)} className="mt-1" placeholder="e.g. Special Collection" data-testid="new-issue-title" /></div>
        </div>
        <DialogFooter><Button onClick={create} disabled={busy} className="rounded-full" data-testid="new-issue-create">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create Issue"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function IssuesAdmin() {
  const [journals, setJournals] = useState([]);
  const [slug, setSlug] = useState("");
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/journals").then(({ data }) => {
      setJournals(data);
      const withPapers = data.find((j) => (j.paper_count || 0) > 0);
      if (withPapers || data[0]) setSlug((withPapers || data[0]).slug);
    });
  }, []);

  const load = (s) => {
    setLoading(true);
    api.get(`/journals/${s}/issues`).then(({ data }) => setIssues(data)).finally(() => setLoading(false));
  };

  useEffect(() => { if (slug) load(slug); /* eslint-disable-next-line */ }, [slug]);

  return (
    <div data-testid="issues-admin">
      <h1 className="font-serif font-bold text-2xl text-slate-900 dark:text-slate-100 mb-1 flex items-center gap-2">
        <Layers className="w-6 h-6 text-amber-600" /> Issue Manager
      </h1>
      <p className="text-slate-500 text-sm mb-5">Create volumes and issues, add a cover and editorial note, and drag articles to set the table of contents.</p>

      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-6">
        <div className="max-w-sm flex-1">
          <Select value={slug} onValueChange={setSlug}>
            <SelectTrigger data-testid="issues-journal-select"><SelectValue placeholder="Select a journal" /></SelectTrigger>
            <SelectContent>{journals.map((j) => <SelectItem key={j.slug} value={j.slug}>{j.title}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        {slug && <AddIssueDialog journalSlug={slug} reload={() => load(slug)} />}
      </div>

      {loading ? (
        <div className="py-16 grid place-items-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
      ) : issues.length === 0 ? (
        <div className="text-center py-16 bg-card rounded-xl border border-dashed border-slate-300 dark:border-slate-700">
          <BookOpen className="w-10 h-10 mx-auto text-slate-300 mb-3" />
          <p className="text-slate-500">No volumes or issues yet. Create one or publish a paper to this journal.</p>
        </div>
      ) : (
        <div className="space-y-5">
          {issues.map((it) => <IssueCard key={`${it.volume}-${it.issue}`} journalSlug={slug} issue={it} reload={() => load(slug)} />)}
        </div>
      )}
    </div>
  );
}
